/**
 * Watch Tracker Service
 *
 * Extracted from player.elements.js WatchTracker component.
 * Provides standalone watch time reporting functionality
 * that can be used by the player and other components.
 *
 * Reports watch progress to CMS API for:
 * - Continue Watch feature
 * - Watch history
 * - Analytics
 */
"use client";

import axiosInstance from "@/config/axiosInstance";
import { getCMSApiRoute } from "@/helpers/api.util";

// Minimum watch time before reporting (in seconds)
const MIN_REPORT_THRESHOLD = 60;

// Report interval (in seconds)
const REPORT_INTERVAL = 60;

/**
 * Report watch time to API
 *
 * @param {Object} params - Report parameters
 * @param {string} params.contentKey - Content slug
 * @param {string} params.contentKind - Content type (movie, series, program)
 * @param {number} [params.season] - Season number (for series/programs)
 * @param {number} [params.episode] - Episode number (for series/programs)
 * @param {number} params.time - Watch time in seconds
 * @returns {Promise<boolean>} Success status
 */
export async function reportWatchTime({
    contentKey,
    contentKind,
    season,
    episode,
    time,
}) {
    // Convert to minutes, skip if less than 1 minute
    const timeInMinutes = Math.floor(time / 60);
    if (timeInMinutes < 1) return false;

    try {
        const isEpisode =
            contentKind === "series" || contentKind === "program" ? 1 : 0;

        await axiosInstance.post(getCMSApiRoute("action/watch-tracker"), {
            contentKey,
            isEpisode,
            season: season || undefined,
            episode: episode || undefined,
            time: timeInMinutes,
        });

        console.log(
            `[WatchTracker] Reported ${timeInMinutes}m for ${contentKey}${season ? ` S${season}E${episode}` : ""
            }`
        );
        return true;
    } catch (error) {
        console.error("[WatchTracker] Report failed:", error);
        return false;
    }
}

/**
 * Get watch tracker data (resume position) for content
 *
 * @param {Object} params - Query parameters
 * @param {string} params.contentKey - Content slug
 * @param {boolean} [params.isEpisode] - Whether content is episode
 * @param {number} [params.season] - Season number
 * @param {number} [params.episode] - Episode number
 * @returns {Promise<Object>} Watch tracker data with elapsedTime
 */
export async function getWatchTrackerData({
    contentKey,
    isEpisode,
    season,
    episode,
}) {
    try {
        const queryParams = new URLSearchParams(
            Object.entries({ contentKey, isEpisode, season, episode }).filter(
                ([, v]) => v != null
            )
        );

        const response = await axiosInstance.get(
            getCMSApiRoute(`action/watch-tracker?${queryParams.toString()}`)
        );

        const { data } = response.data;
        return data?.results?.[0] || data?.[0] || {};
    } catch (error) {
        console.log("[WatchTracker] Get tracker data error:", error);
        return {};
    }
}

/**
 * Get every saved episode position for the active profile and a series.
 *
 * @param {Object} params
 * @param {string} params.contentKey - Series slug
 * @param {AbortSignal} [params.signal] - Optional request cancellation signal
 * @returns {Promise<Array<Object>>} Saved episode progress rows
 */
export async function getSeriesWatchProgress({ contentKey, signal }) {
    const response = await axiosInstance.get(
        getCMSApiRoute("action/watch-tracker/series"),
        {
            params: { contentKey },
            signal,
        }
    );

    const results = response.data?.data?.results;
    return Array.isArray(results) ? results : [];
}

/**
 * Build a collision-safe lookup key. Episode numbers repeat across seasons.
 */
export function getEpisodeProgressKey(seasonNumber, episodeNumber) {
    return `${Number(seasonNumber)}:${Number(episodeNumber)}`;
}

/**
 * Index series progress by both season and episode.
 */
export function indexSeriesWatchProgress(results = []) {
    return new Map(
        results.map((item) => [
            getEpisodeProgressKey(item.seasonNumber, item.episodeNumber),
            item,
        ])
    );
}

/**
 * Convert saved position and duration to a safe visual percentage.
 */
export function getWatchProgressPercent(progress) {
    const elapsed = Number(progress?.elapsedTime);
    const total = Number(progress?.totalTime);

    if (!Number.isFinite(elapsed) || !Number.isFinite(total) || elapsed <= 0 || total <= 0) {
        return 0;
    }

    return Math.min(100, Math.max(0, (elapsed / total) * 100));
}

/**
 * Check if current watch time should trigger a report
 * Reports every REPORT_INTERVAL seconds after MIN_REPORT_THRESHOLD
 *
 * @param {number} currentTime - Current playback time in seconds
 * @returns {boolean} Whether to report
 */
export function shouldReport(currentTime) {
    const intTime = Math.floor(currentTime);
    return intTime >= MIN_REPORT_THRESHOLD * 2 && intTime % REPORT_INTERVAL === 0;
}

/**
 * Calculate resume position from watch tracker data
 * Subtracts 5 seconds for context
 *
 * @param {Object} watchTrackData - Watch tracker data from API
 * @returns {number|null} Resume position in seconds, or null
 */
export function getResumePosition(watchTrackData) {
    const elapsedTime = watchTrackData?.options?.elapsedTime;
    if (!elapsedTime || elapsedTime <= 0) return null;

    // Convert minutes to seconds and subtract 5s for context
    return Math.max(0, Number(elapsedTime * 60) - 5);
}

const watchTrackerService = {
    reportWatchTime,
    getWatchTrackerData,
    getSeriesWatchProgress,
    getEpisodeProgressKey,
    indexSeriesWatchProgress,
    getWatchProgressPercent,
    shouldReport,
    getResumePosition,
    MIN_REPORT_THRESHOLD,
    REPORT_INTERVAL,
};

export default watchTrackerService;
